import { HARD_SIGNAL_LABELS } from '../../platform-finance/health-strip';

// The Overview tab's arithmetic, with no React in it (spec 2026-09-24
// "The Overview tab"): four tile figures and two short lists out of the
// four reads the tabs already make. Pure, so the rules — positive balances
// only count as owed, lists cap at five, figures count everyone — are
// pinned without rendering anything.
export const LIST_CAP = 5;

// Shared with payouts-tab.js: a balance under a cent is noise, never owed
// and never worth a row.
export const BALANCE_THRESHOLD = 0.01;

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// The health strip's own reading (health-strip.js): `reason` is set only
// when the read is unreadable (PlatformHealthProjection.cs nulls it on a
// readable result), so a hard signal is named from `hardSignals` via the
// same label map the strip itself uses — never from `reason`.
const healthOf = (health) => {
  if (!health) return { tone: 'slate', label: 'Unknown', feesWaived: 0 };
  const feesWaived = round2(health.feesNotCollected);
  if (health.readable === false) return { tone: 'slate', label: 'Health could not be checked', feesWaived };
  if (health.hasHardSignal) {
    const hardSignals = Array.isArray(health.hardSignals) ? health.hardSignals : [];
    const [first, ...rest] = hardSignals;
    const describe = first ? HARD_SIGNAL_LABELS[first] : null;
    const label = describe ? `${describe(health)}${rest.length ? ` +${rest.length} more` : ''}` : 'Critical';
    return { tone: 'rose', label, feesWaived };
  }
  return { tone: 'emerald', label: 'Healthy', feesWaived };
};

// This month's finance rows, folded for the chart: the biggest companies
// by gross (a company that ran on both models this period has two rows and
// is one bar). No legacy/Connect split (owner, 2026-09-24).
const TOP_CAP = 6;
const chartsOf = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  const byCompany = new Map();
  for (const r of list) {
    if (r?.error) continue;
    const gross = Number(r.grossAmount) || 0;
    const net = Number(r.ourShare) || 0;
    const key = r.companyId || r.companyName;
    const acc = byCompany.get(key) || { name: r.companyName || key, gross: 0, net: 0 };
    acc.gross += gross;
    acc.net += net;
    byCompany.set(key, acc);
  }
  const top = [...byCompany.values()]
    .filter((c) => c.gross > 0)
    .sort((a, b) => b.gross - a.gross)
    .slice(0, TOP_CAP)
    .map((c) => ({ name: c.name, gross: round2(c.gross), net: round2(c.net) }));
  return { top };
};

// An account row is "started" unless the API said it is not (an older API
// sent only started ones, with no flag).
const started = (a) => a.hasAccount !== false;

export const summarize = ({ totals, rows: financeRows, balances, accounts, health }) => {
  const rows = Array.isArray(balances) ? balances : [];
  // A balance under the threshold is noise, not a payable — filtered out of
  // both the owed figure and the listed rows the same way, from the same
  // thresholded set, so the two can never disagree about a sub-cent balance.
  const listed = rows
    .filter((r) => Math.abs(Number(r.balance) || 0) >= BALANCE_THRESHOLD)
    .sort((a, b) => (Number(b.balance) || 0) - (Number(a.balance) || 0));
  const owedRows = listed.filter((r) => (Number(r.balance) || 0) > 0);

  const all = Array.isArray(accounts) ? accounts : [];
  const attention = all
    .filter((a) => started(a) && (a.outstanding || 0) > 0)
    .sort((a, b) => b.outstanding - a.outstanding);

  return {
    revenue: { net: round2(totals?.ourShare), gross: round2(totals?.grossAmount) },
    owed: { total: round2(owedRows.reduce((sum, r) => sum + (Number(r.balance) || 0), 0)), companies: owedRows.length },
    connect: {
      total: all.length,
      direct: all.filter((a) => a.paymentModel === 'CONNECT_DIRECT').length,
      notStarted: all.filter((a) => !started(a)).length,
      attention: attention.length,
    },
    health: healthOf(health),
    charts: chartsOf(financeRows),
    toPay: listed.slice(0, LIST_CAP).map(({ id, name, balance, logo }) => ({ id, name, balance: round2(balance), logo: logo || null })),
    attention: attention.slice(0, LIST_CAP).map(({ companyId, companyName, outstanding, paymentModel }) => ({ companyId, companyName, outstanding, paymentModel })),
  };
};
